import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandFavicon } from '../components/BrandFavicon';
import { RelationshipBadge } from '../components/visibility/VisibilityBadges';
import { VisibilityBar } from '../components/visibility/VisibilityBadges';
import { PromptTopBrands } from '../components/prompts/PromptTopBrands';
import { getHelpDescription } from '../utils/help/help-content';
import type {
  ProjectVisibilityProviderBreakdown,
  ProjectVisibilityPromptPerformance,
  ProjectVisibilityTopBrand
} from '../types';

export interface VisibilityBrandRow extends ProjectVisibilityTopBrand {
  aiMentionsResolved: number;
  aiUsageLabel: string;
  promptCoverageLabel: string;
}

export function getVisibilityBrandColumns(onOpenSources: (row: VisibilityBrandRow) => void) {
  return [
    {
      field: 'canonicalName',
      type: 'string',
      title: 'برند',
      minWidth: 220,
      flex: 1,
      cellRenderer: (params: { data: VisibilityBrandRow }) => (
        <div className="d-flex align-items-center gap-2 h-100">
          <BrandFavicon
            host={params.data.domain}
            faviconUrl={params.data.faviconUrl}
            name={params.data.canonicalName}
            relationship={params.data.relationship}
          />
          <span className="fw-medium text-1000 text-truncate">{params.data.canonicalName}</span>
        </div>
      )
    },
    {
      field: 'relationship',
      type: 'string',
      title: 'رابطه',
      width: 110,
      cellRenderer: (params: { value: ProjectVisibilityTopBrand['relationship'] }) => (
        <div className="w-100 d-flex h-100 align-items-center">
          <RelationshipBadge relationship={params.value} solid={params.value === 'known'} />
        </div>
      )
    },
    {
      field: 'visibilityScore',
      type: 'number',
      title: 'دیده‌شدن در AI',
      width: 160,
      cellRenderer: (params: { value: number }) => (
        <div className="w-100 d-flex h-100 align-items-center">
          <VisibilityBar score={params.value} />
        </div>
      )
    },
    {
      field: 'aiMentionsResolved',
      type: 'number',
      title: 'منشن در AI',
      width: 120
    },
    {
      field: 'aiUsageLabel',
      type: 'string',
      title: 'استفاده در AI',
      width: 130
    },
    {
      field: 'promptCoverageLabel',
      type: 'string',
      title: 'پوشش پرامپت',
      width: 130
    },
    {
      field: 'avgPosition',
      type: 'number',
      title: 'میانگین رتبه AI',
      width: 140,
      cellRenderer: (params: { value: number | null }) =>
        params.value != null ? `#${params.value}` : '—'
    },
    {
      field: 'sources',
      title: 'منابع',
      width: 80,
      sortable: false,
      cellRenderer: (params: { data: VisibilityBrandRow }) => (
        <div className="w-100 d-flex h-100 align-items-center justify-content-center">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`brand-sources-${params.data.canonicalName}`}>{getHelpDescription('actions.brand_sources')}</Tooltip>}
          >
            <Button
              variant="falcon-default"
              size="sm"
              className="px-2"
              onClick={() => onOpenSources(params.data)}
              aria-label="منابع برند"
            >
              <FontAwesomeIcon icon="link" />
            </Button>
          </OverlayTrigger>
        </div>
      )
    }
  ];
}

export interface VisibilityPromptRow extends ProjectVisibilityPromptPerformance {
  computedVisibility: number | null;
}

export const visibilityPromptPerformanceColumns = [
  {
    field: 'text',
    type: 'string',
    title: 'پرامپت',
    minWidth: 260,
    flex: 1,
    cellRenderer: (params: { value: string }) => (
      <div className="w-100 h-100 d-flex align-items-center text-truncate">{params.value}</div>
    )
  },
  {
    field: 'computedVisibility',
    type: 'number',
    title: 'دیده‌شدن در AI',
    width: 160,
    cellRenderer: (params: { value: number | null; data: VisibilityPromptRow }) => (
      <div className="w-100 d-flex h-100 align-items-center">
        <VisibilityBar score={params.value ?? params.data.visibilityScore} />
      </div>
    )
  },
  {
    field: 'brandsFound',
    type: 'number',
    title: 'برندها',
    width: 100
  },
  {
    field: 'topBrands',
    title: 'برندهای برتر',
    width: 150,
    sortable: false,
    cellRenderer: (params: { data: VisibilityPromptRow }) => (
      <div className="w-100 d-flex h-100 align-items-center">
        <PromptTopBrands
          status={params.data.status}
          brands={params.data.topBrands}
          brandsFound={params.data.brandsFound}
        />
      </div>
    )
  }
];

export const providerBreakdownColumns = [
  {
    field: 'modelName',
    type: 'string',
    title: 'مدل',
    minWidth: 180,
    flex: 1
  },
  {
    field: 'executions',
    type: 'number',
    title: 'اجراها',
    width: 120
  },
  {
    field: 'avgVisibility',
    type: 'number',
    title: 'میانگین دیده‌شدن AI',
    width: 180,
    cellRenderer: (params: { value: number; data: ProjectVisibilityProviderBreakdown }) => (
      <div className="w-100 d-flex h-100 align-items-center">
        <VisibilityBar score={params.value} />
      </div>
    )
  },
  {
    field: 'brandsFound',
    type: 'number',
    title: 'برندهای یافت‌شده',
    width: 150
  }
];
